<template>
  <!-- Add Device Dialog -->
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card style="width: 600px; max-width: 80vw;">
      <q-card-section>
        <div class="text-h6">Add Device to {{ selectedEquipment?.name }}</div>
      </q-card-section>

      <q-card-section>
        <q-form @submit.prevent="handleSubmit">
          <!-- Template Selection - Now at the TOP -->
          <q-select
            :model-value="formData.selectedTemplate"
            :options="allTemplateOptions"
            label="Select Template (Optional)"
            filled
            clearable
            class="q-mb-md"
            hint="Choose a template to create a device"
            @update:model-value="handleTemplateSelect"
          >
            <template #prepend>
              <q-icon name="inventory_2" color="primary" />
            </template>
            <template #selected>
              <q-item v-if="formData.selectedTemplate" dense>
                <q-item-section v-if="formData.selectedTemplate.type === 'compound'" avatar>
                  <q-icon name="link" color="orange" size="sm" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ formData.selectedTemplate.label }}</q-item-label>
                </q-item-section>
                <q-item-section v-if="formData.selectedTemplate.type === 'compound'" side>
                  <q-badge color="orange" label="Compound" />
                </q-item-section>
              </q-item>
            </template>
            <template #option="scope">
              <!-- Separator -->
              <q-separator v-if="scope.opt.separator" />
              <!-- Header -->
              <q-item-label v-else-if="scope.opt.type === 'header'" header class="text-weight-bold text-primary">
                {{ scope.opt.label }}
              </q-item-label>
              <!-- Template Item -->
              <q-item v-else v-bind="scope.itemProps">
                <q-item-section v-if="scope.opt.type === 'compound'" avatar>
                  <q-icon name="link" color="orange" />
                </q-item-section>
                <q-item-section v-else-if="scope.opt.type === 'simple'" avatar>
                  <q-icon name="developer_board" color="blue" />
                </q-item-section>
                <q-item-section>
                  <q-item-label>{{ scope.opt.label }}</q-item-label>
                  <q-item-label v-if="scope.opt.description" caption>
                    {{ scope.opt.description }}
                  </q-item-label>
                  <q-item-label v-if="scope.opt.component_types && scope.opt.component_types.length > 0" caption>
                    <q-icon name="widgets" size="xs" /> Components: {{ scope.opt.component_types.join(', ') }}
                  </q-item-label>
                </q-item-section>
                <q-item-section v-if="scope.opt.type === 'compound'" side>
                  <q-badge color="orange" label="Compound" />
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Separator -->
          <q-separator class="q-my-md" />

          <div class="text-subtitle2 q-mb-md">Device Details</div>

          <!-- Device Details - Below template selection -->
          <q-input
            :model-value="formData.name"
            :label="formData.selectedTemplate?.type === 'compound' ? 'Assembly Name' : 'Device Name'"
            filled
            required
            class="q-mb-md"
            :hint="formData.selectedTemplate?.type === 'compound' ? 'This name will be used as prefix for all components' : ''"
            @update:model-value="$emit('update:name', $event)"
          />
          <q-select
            v-if="!formData.selectedTemplate || formData.selectedTemplate.type !== 'compound'"
            :model-value="formData.brick_class"
            :options="deviceBrickClasses"
            label="Device Type (BRICK Class)"
            filled
            required
            emit-value
            map-options
            class="q-mb-md"
            @update:model-value="$emit('update:brick-class', $event)"
          />
          <!-- Device Part Registry -->
          <q-separator class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">
            <q-icon name="inventory" class="q-mr-xs" />
            Device Part (Optional)
          </div>

          <q-select
            v-model="selectedDevicePart"
            :options="partOptionsWithAdd"
            label="Search Device Part"
            filled
            use-input
            clearable
            option-label="display"
            hint="Search by part number, manufacturer, or model"
            class="q-mb-md"
            @filter="filterParts"
            @update:model-value="handlePartSelected"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>

            <template #no-option>
              <q-item>
                <q-item-section class="text-grey">
                  Type 2+ characters to search...
                </q-item-section>
              </q-item>
            </template>

            <template #option="scope">
              <q-item v-bind="scope.itemProps">
                <q-item-section v-if="scope.opt.isAddNew">
                  <q-item-label>
                    <q-icon name="add_circle" color="primary" class="q-mr-sm" />
                    {{ scope.opt.display }}
                  </q-item-label>
                </q-item-section>
                <q-item-section v-else>
                  <q-item-label>{{ scope.opt.part_number }}</q-item-label>
                  <q-item-label caption>{{ scope.opt.manufacturer }} - {{ scope.opt.model }}</q-item-label>
                </q-item-section>
              </q-item>
            </template>
          </q-select>

          <!-- Selected Device Part Info Card -->
          <q-card v-if="selectedDevicePart && !selectedDevicePart.isAddNew" flat bordered class="q-mb-md bg-blue-1">
            <q-card-section class="q-pa-md">
              <div class="text-subtitle2 q-mb-sm">Selected Part Details</div>
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <div class="text-caption text-grey-7">Part Number</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.part_number }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">Manufacturer</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.manufacturer }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">Model</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.model }}</div>
                </div>
                <div class="col-6">
                  <div class="text-caption text-grey-7">BRICK Class</div>
                  <div class="text-weight-medium">{{ selectedDevicePart.brick_class }}</div>
                </div>
              </div>
            </q-card-section>
          </q-card>

          <!-- Physical Location Section -->
          <q-separator class="q-my-md" />
          <div class="text-subtitle2 q-mb-md">Physical Location (Optional)</div>
          <div class="text-caption text-grey-7 q-mb-md">
            If this device is physically located in a different space than its equipment
          </div>
          <q-field filled label="Installation Location" stack-label class="q-mb-md" hint="Where is this device physically installed?">
            <template #control>
              <div class="full-width q-py-sm">
                <q-breadcrumbs v-if="deviceLocationPath.length > 0">
                  <q-breadcrumbs-el
                    v-for="(item, index) in deviceLocationPath"
                    :key="index"
                    :label="item.label"
                    :icon="item.icon"
                  />
                </q-breadcrumbs>
                <span v-else class="text-grey-6">Select location...</span>
              </div>
            </template>
            <template #append>
              <q-btn
                flat
                dense
                icon="edit_location"
                @click="$emit('open-location-picker', 'add')"
              >
                <q-tooltip>Change Location</q-tooltip>
              </q-btn>
              <q-btn
                v-if="formData.location_space_id"
                flat
                dense
                icon="clear"
                @click="$emit('clear-location', 'add')"
              >
                <q-tooltip>Clear Location</q-tooltip>
              </q-btn>
            </template>
          </q-field>
          <q-input
            :model-value="formData.location_description"
            label="Location Details"
            filled
            type="textarea"
            rows="2"
            class="q-mb-md"
            hint="e.g., 'North wall at 1.5m height', 'Above ceiling tile near window'"
            @update:model-value="$emit('update:location-description', $event)"
          />
        </q-form>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat label="Cancel" @click="$emit('update:modelValue', false)" />
        <q-btn color="primary" label="Add Device" :loading="saving" @click="handleSubmit" />
      </q-card-actions>
    </q-card>

    <!-- Add New Part Dialog (Shared Component) -->
    <AddDevicePartDialog
      v-model="showAddPartDialog"
      :part-number="addPartInitialData.partNumber"
      :brick-class="addPartInitialData.brickClass"
      @created="handlePartCreated"
    />
  </q-dialog>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { authenticatedFetch } from '@/utils/apiHelper';
  import AddDevicePartDialog from './AddDevicePartDialog.vue';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      required: true,
    },
    formData: {
      type: Object,
      required: true,
    },
    selectedEquipment: {
      type: Object,
      default: null,
    },
    allTemplateOptions: {
      type: Array,
      default: () => [],
    },
    deviceBrickClasses: {
      type: Array,
      required: true,
    },
    deviceLocationPath: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:name',
    'update:brick-class',
    'update:device-part-id',
    'update:location-description',
    'template-select',
    'open-location-picker',
    'clear-location',
    'submit',
  ]);

  // Device Part state
  const selectedDevicePart = ref(null);
  const partOptions = ref([]);
  const currentSearchTerm = ref('');

  // Add New Part dialog state
  const showAddPartDialog = ref(false);
  const addPartInitialData = ref({
    partNumber: '',
    brickClass: '',
  });

  // Filter parts
  const filterParts = async (val, update, abort) => {
    currentSearchTerm.value = val;

    if (val.length < 2) {
      abort();
      return;
    }

    try {
      const params = new URLSearchParams({ q: val });
      const response = await authenticatedFetch(`/api/device-parts/search?${params}`);
      const data = await response.json();

      update(() => {
        partOptions.value = (data.data || []).map((part) => ({
          ...part,
          display: `${part.part_number} - ${part.manufacturer} ${part.model}`,
        }));
      });
    } catch (error) {
      abort();
    }
  };

  // Computed: Add "Add New" option
  const partOptionsWithAdd = computed(() => {
    const options = [...partOptions.value];

    if (currentSearchTerm.value?.length >= 2 && options.length === 0) {
      options.push({
        id: '__add_new__',
        isAddNew: true,
        display: `➕ Add "${currentSearchTerm.value}" as new part`,
        searchTerm: currentSearchTerm.value,
      });
    }

    return options;
  });

  // Handle part selection
  const handlePartSelected = (part) => {
    if (!part) {
      selectedDevicePart.value = null;
      emit('update:device-part-id', null);
      return;
    }

    if (part.isAddNew) {
      // Open add part dialog with pre-filled data
      addPartInitialData.value = {
        partNumber: part.searchTerm,
        brickClass: props.formData.brick_class || '',
      };
      showAddPartDialog.value = true;
      selectedDevicePart.value = null;
      return;
    }

    selectedDevicePart.value = part;
    emit('update:device-part-id', part?.id || null);
  };

  // Handle part created from shared dialog
  const handlePartCreated = (createdPart) => {
    // Add display property for the part selector
    const partWithDisplay = {
      ...createdPart,
      display: `${createdPart.part_number} - ${createdPart.manufacturer} ${createdPart.model}`,
    };

    // Auto-select the newly created part
    selectedDevicePart.value = partWithDisplay;
    emit('update:device-part-id', createdPart.id);
  };

  const handleTemplateSelect = (template) => {
    emit('template-select', template);
  };

  const handleSubmit = () => {
    emit('submit');
  };
</script>
